<script lang="ts" setup>
import { provide, ref, computed, toRaw, onMounted } from 'vue'

import { breakpointsTailwind, useBreakpoints } from '@vueuse/core'
import RoomieButton from '@lahaus-roomie/roomie/src/components/RoomieButton/index.vue'
import type { InvestmentProject, Typology } from '@/server/api/buyer-front/types'

import LockOverlay from '@/components/LockOverlay/index.vue'
import PdHero from '@/components/pd/HeroV2/index.vue'
import QuotationPlanTypologies from '@/components/QuotationPlan/TypologiesV2/index.vue'
import LazyAppGallery from '@/components/AppGalleryV2/index.vue'
import ShareAndEarnBanner from '@/components/pd/ShareAndEarnBanner/index.vue'
import ExploreProjectSection from '@/components/pd/ExploreProjectSection/index.vue'
import VerificationBanner from '@/components/pd/VerificationBanner/index.vue'
import AppMapStatic from '@/components/App/Map/StaticV2/index.vue'
import FeatureDetail from '@/components/pd/FeatureDetail/index.vue'
import PdAmenities from '@/components/pd/AmenitiesV2/index.vue'
import InterestedSection from '@/components/pd/InterestedSection/index.vue'
import PdRecommendationsV2 from '@/components/pd/RecommendationsV2/index.vue'
import AppChatV2 from '@/components/App/ChatV2/index.vue'
import PdFixedCtasV2 from '@/components/pd/PdFixedCtasV2/index.vue'
import { useAppChatStore } from '@/stores/appChatStore'
import { useUserStore } from '@/stores/userStore'
import { getIsInPersonVisitActive } from '@/utils/project'
import { whatsappListingNumbers } from '@/config/whatsappListingNumbers'
import type { LocaleCode } from '@/config/locale-domains'

import { LazyAppModal } from '#components'

import { investmentProjectKey } from '@/symbols/listing'

import { SCREEN } from '@/utils/segment'

const route = useRoute()
const breakpoints = useBreakpoints(breakpointsTailwind)
const { $sobreplanosDataService, $listingDataService, $trackEvent, $config, $useLhId, $whatsappNumbers } = useNuxtApp()

const { t, tm, rt, locale } = useI18n()

const appChatStore = useAppChatStore()
const userStore = useUserStore()
const { buildImageUrl } = useImsImageProvider()

const optionalParams = {
  refresh: route.query.refresh === 'true'
}

const slug = route.params.slug as string
const isDemo = route.query.mode === 'demo'
const { data: mainDataRef, error } = await useAsyncData<InvestmentProject>('mainDataRef', () => $sobreplanosDataService.getListing(slug, optionalParams.refresh, isDemo))
const imsListing = await $listingDataService.getListing(slug)
const legalInfo = imsListing?.rows?.[0]?.legal

if (error.value || !mainDataRef.value?.code) {
  throw createError({
    statusCode: 404,
    statusMessage: 'Not found',
    fatal: true
  })
}

const listing = toRaw(mainDataRef.value!)
const coverImage = computed(() => listing.cover ? buildImageUrl(listing.cover) : 'https://lahaus.imgix.net/static/default/default-share-social-media-v2.jpg')

const isInPersonVisitActive = computed(() => {
  const listingId = listing.code
  const businessHubCode = listing.raw_listing?.location?.business_hub_code
  return getIsInPersonVisitActive(listingId, businessHubCode, $config)
})
const isSamStickyEnabled = ref(false)
const appChat = ref()
const canShowSamSticky = computed(() => isSamStickyEnabled.value)
const isBottomCtaVisible = ref(true)

useSeoMeta({
  robots: 'index, follow',
  title: `Conoce ${listing.name} en ${listing.city_label}, ${listing.raw_listing?.location.neighborhood}`,
  ogTitle: `Conoce ${listing.name} en ${listing.city_label}, ${listing.raw_listing?.location.neighborhood}`,
  description: `Conoce ${listing.name} en ${listing.city_label}, ${listing.raw_listing?.location.neighborhood} ¡invierte en bienes raíces y benefíciate de la renta hotelera y turística!`,
  ogDescription: `Conoce ${listing.name} en ${listing.city_label}, ${listing.raw_listing?.location.neighborhood} ¡invierte en bienes raíces y benefíciate de la renta hotelera y turística!`,
  ogImage: coverImage,
  ogSiteName: 'La Haus',
  twitterCard: 'summary'
})

provide(investmentProjectKey, listing)

const showGallery = ref(false)
const opacityValue = ref(false)
const isDesktop = breakpoints.greater('md')

const handleOpenGallery = (tabName: string) => {
  currentTab.value = tabName
  showGallery.value = true
}

const handleCloseGallery = () => {
  showGallery.value = false
}

const currentTab = ref('pictures')

const { isLoginWallEnabled } = useLoginWall()

const isContentLocked = computed(() => {
  if (!isLoginWallEnabled) return false
  const referrerId = route.query.referrer_id as string
  return !userStore.isAuthenticated && !referrerId
})

const initializeChat = (listing: { name: string }) => {
  appChatStore.updateMessagesChips(tm('samGpt.chipsPdp')?.map(chip => ({
    id: rt(chip.id),
    title: rt(chip.title),
    message: rt(chip.message),
    type: rt(chip.type)
  })))
}

const trackListingViewEvent = (listing: { code: string }, cookieData: any) => {
  $trackEvent({
    name: 'listing_viewed',
    version: '5-0-0',
    data: {
      listings: [
        {
          id: listing.code,
          source: 'PROPERTY_CATALOG'
        }
      ],
      url: window.location.href,
      view_platform: 'WEB',
      collection_id: cookieData?.collection_id || '',
      collection_position: cookieData?.index || 0
    }
  })
}

const clearCollectionCookie = (cookieName: string) => {
  const collectionCookie = useCookie(cookieName)
  collectionCookie.value = null
}

onMounted(() => {
  const cookieName = `listingCollection_last_card_${listing.code}`
  const collectionCookie = useCookie(cookieName)
  const cookieData = collectionCookie.value

  initializeChat(listing)
  trackListingViewEvent(listing, cookieData)
  clearCollectionCookie(cookieName)
})

const getImages = (imageType:string) => {
  if (!listing.pictures_v2) return []

  return listing.pictures_v2
    .filter(image => image.type === imageType)
    .sort((a, b) => a.order - b.order)
    .map(image => image.url)
}

const allImages = computed(() => {
  const gallery = getImages('gallery')
  const blueprints = getImages('blueprints')

  return [
    listing.cover,
    ...gallery,
    ...blueprints
  ]
})

const videos = computed(() => {
  const list = listing.raw_listing?.attributes?.videos
  if (!list || list?.length === 0) return undefined
  else return list
})

const webinars = computed(() => {
  return listing.ims?.webinars_urls
})

const tours = computed(() => {
  return listing.raw_listing?.attributes?.tour
})

const locationParams = {
  lat: listing.raw_listing?.location?.latitude,
  lng: listing.raw_listing?.location?.longitude,
  mapId: `dynamic-google-map-${listing.code}`
}

const toggleOpacity = (value: boolean) => {
  opacityValue.value = value
}

const canShowQuotationPlans = computed(() => !!listing.ims?.typologies)

const isUSListing = computed(() => {
  const country = listing.raw_listing?.location?.country
  return country === 'Estados Unidos'
})

const disableScheduleVisitCta = ref(false)

const scheduleVisitWhatsappHref = computed(() => {
  const number = whatsappListingNumbers[slug] ?? ($whatsappNumbers as Record<LocaleCode, string>)[locale.value as LocaleCode]
  const text = encodeURIComponent(`Hola me gustaría más información sobre ${listing.name}`)
  return `https://wa.me/${number}?text=${text}`
})

const projectLatitude = computed(() => listing.raw_listing?.location?.latitude)
const projectLongitude = computed(() => listing.raw_listing?.location?.longitude)
const projectLocationName = computed(() => `${listing.raw_listing?.location?.state}, ${listing.neighborhood_label}`)
const isMobile = breakpoints.greater('sm')

const propertySchema = computed(() => {
  const baseSchema = {
    '@context': 'https://schema.org',
    '@type': 'RealEstateListing',
    name: listing.name,
    description: listing.description,
    url: `${$config.public.siteUrl}${route.fullPath}`,
    image: listing.cover ? buildImageUrl(listing.cover) : '',
    offers: [] as any[]
  }

  // Add an offer for each typology if available
  if (listing.ims?.typologies?.length > 0) {
    baseSchema.offers = (listing.ims.typologies as Typology[]).map(typology => ({
      '@type': 'Offer',
      priceCurrency: listing.currency,
      price: typology.price,
      priceValidUntil: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
      businessFunction: 'http://purl.org/goodrelations/v1#sell',
      itemOffered: {
        '@type': 'Accommodation',
        name: `${listing.name} - ${typology.name}`,
        description: listing.description,
        numberOfRooms: typology.bedroom,
        numberOfBathroomsTotal: typology.bathroom,
        floorSize: {
          '@type': 'QuantitativeValue',
          value: typology.min_area,
          unitText: 'm²'
        },
        address: {
          '@type': 'PostalAddress',
          addressLocality: listing.neighborhood_label,
          addressRegion: listing.raw_listing?.location?.state,
          addressCountry: listing.raw_listing?.location?.country,
          streetAddress: listing.raw_listing?.address
        },
        geo: {
          '@type': 'GeoCoordinates',
          latitude: projectLatitude.value,
          longitude: projectLongitude.value
        }
      }
    }))
  }

  return baseSchema
})

useHead({
  script: [{
    type: 'application/ld+json',
    innerHTML: JSON.stringify(propertySchema.value)
  }]
})

const goToForm = () => {
  const offset = 190
  const fallbackSelector = '.quotation-plan-fee-simulator'

  const fallbackElement = document.querySelector(fallbackSelector) as HTMLElement
  const elementPaymentPlan = document.querySelector('#pd-quotation-plan-table') as HTMLElement
  const element = elementPaymentPlan || fallbackElement

  if (!element) return

  const y = element.getBoundingClientRect().top + window.scrollY - offset
  window.scrollTo({ top: y, behavior: 'smooth' })
}

const updateBottomCtaVisibility = (isVisible: boolean) => {
  isBottomCtaVisible.value = isVisible
}

const showFeatureDetailSection = ref(false)

showFeatureDetailSection.value = !!listing?.raw_listing?.ims?.fiduciary
showFeatureDetailSection.value &&= !!legalInfo?.estimated_administration_price
showFeatureDetailSection.value &&= !!legalInfo?.project_stages
showFeatureDetailSection.value &&= !!legalInfo?.socioeconomic_stratum
showFeatureDetailSection.value &&= !!legalInfo?.finishes_and_details?.description
showFeatureDetailSection.value &&= !!legalInfo?.parking_details
showFeatureDetailSection.value &&= !!legalInfo?.penalties_and_surcharges

</script>

<script lang="ts">
export default {
  name: 'PagePdCitySlugV2'
}
</script>

<template>
  <main
    class="page-pd-v2-city-slug overflow-x-hidden mb-56"
    :class="{ 'overflow-hidden': isContentLocked }">
    <LockOverlay
      v-if="isContentLocked"
      :screen="SCREEN.PDP_INVESTORS"
      :listing-id="listing.code"
      :should-enqueue-twilio-contact="listing.ims?.external_status === 'active'"
      class="z-40 fixed h-screen bottom-0" />

    <PdHero
      :class="{ 'with-tag': !canShowSamSticky }"
      :listing="listing"
      :screen="SCREEN.PDP_INVESTORS"
      :show-features-summary="!canShowSamSticky"
      @open-gallery="handleOpenGallery" />

    <ShareAndEarnBanner :data-attributes="{dataLh: { P: SCREEN.PDP_INVESTORS, Z: 'referidos'}}" />

    <QuotationPlanTypologies
      v-if="canShowQuotationPlans"
      id="pd-unit-cards"
      :show-blueprint="false"
      class="page-pd-city-slug__section page-pd-v2-city-slug__section--padded"
      :listing-id="listing.code"
      :listing-name="listing.name"
      :listing-type="listing.raw_listing.attributes.product_type"
      :typologies="listing.ims.typologies"
      :disable-schedule-visit-cta="disableScheduleVisitCta"
      :data-attributes="{dataLh: { P: SCREEN.PDP_INVESTORS, Z: 'tipos de aptos'}}"
      :currency="listing.currency"
      :housing-condition="listing?.raw_listing?.housing_condition"
      :is-us-listing="isUSListing">
      <template #contact-form>
        <div
          id="pd-schedule-visit-section">
          <a
            id="pd-schedule-visit-cta"
            class="roomie-button roomie-button--md roomie-button--secondary mt-16 w-full"
            :href="scheduleVisitWhatsappHref"
            target="_blank"
            rel="noopener noreferrer">
            <span class="roomie-button__content">
              <div class="flex items-center gap-8 justify-center cta-16">
                Ajustar plan de pagos
              </div>
            </span>
          </a>

          <p class="text-pine-600 body-3-m lg:body-2-m mt-lh-12" />
        </div>
      </template>
    </QuotationPlanTypologies>

    <section
      id="pd-map-section"
      class="mt-48 lg:mt-56">
      <AppMapStatic
        :id="`pdp-static-map-button-${ listing.code }`"
        class="is-form-visible"
        :lat="projectLatitude"
        :lng="projectLongitude"
        :location-name="projectLocationName"
        :is-mobile="isMobile"
        :address="listing.raw_listing.address"
        button-styles="min-h-[24.4rem] md:min-h-max lg:min-h-[20rem] h-auto"
        img-styles="h-full lg:h-full"
        :data-attributes="{dataLh: { P: SCREEN.PDP_INVESTORS, Z: 'mapa' }}"
        @open-gallery="handleOpenGallery">
        <template
          #location-information="{ isSectionVisible }">
          <template v-if="isInPersonVisitActive">
            <div>
              <h3
                v-sanitize="t('person_visit.title')"
                class="display-6 lg:display-4 text-lime-600" />

              <p class="text-white hl-3-m mt-16 lg:mt-32">
                {{ listing.raw_listing?.location?.state }}, {{ listing.raw_listing?.location?.city }} <br> {{ listing.neighborhood_label }}
              </p>

              <a
                id="pd-in-person-button"
                :href="scheduleVisitWhatsappHref"
                :data-lh-id="$useLhId({ P: SCREEN.PDP_INVESTORS, Z: 'mapa', A: 'ver', C: t('person_visit.cta')})"
                target="_blank"
                rel="noopener noreferrer"
                class="w-full mt-32 md:mt-40 flex items-center justify-center py-12 px-24 border-2 border-white text-white rounded-8 font-medium hover:bg-white hover:text-carbon-800 transition-colors duration-200">
                {{ t('person_visit.cta') }}
              </a>
            </div>
          </template>

          <template v-else>
            <div class="flex flex-col">
              <h3 class="display-6 lg:display-5 text-white">
                {{ projectLocationName }}
              </h3>

              <label
                v-if="isSectionVisible"
                :for="`pdp-static-map-button-${ listing.code }`"
                class="address hl-3-m mt-8 lg:mt-16">
                {{ listing.raw_listing.address }}
              </label>

              <RoomieButton
                id="pd-see-location-cta"
                class="see-location-button relative w-full mt-32 lg:mt-40"
                :data-lh-id="$useLhId({ P: SCREEN.PDP_INVESTORS, A: 'ver', C: t('location.cta'), Z: 'mapa'})"
                variant="secondary"
                size="md"
                @click="handleOpenGallery('location')">
                <span>
                  {{ t('location.cta') }}
                </span>
              </RoomieButton>
            </div>
          </template>
        </template>
      </AppMapStatic>
    </section>

    <ExploreProjectSection
      class="hidden lg:block"
      :listing="listing"
      :data-attributes="{dataLh: { P: SCREEN.PDP_INVESTORS }}"
      @open-gallery="handleOpenGallery" />

    <section
      v-if="showFeatureDetailSection"
      class="feature-detail-section mx-24 mt-48 lg:my-72">
      <FeatureDetail
        :listing="listing"
        :legal-info="legalInfo" />
    </section>

    <InterestedSection
      class="hidden lg:flex"
      :data-attributes="{dataLh: { P: SCREEN.PDP_INVESTORS, Z: 'conoces a alguien interesado' }}"
      ab-testing-key="pdp-referral-banner"
      :source-page="SCREEN.PDP_INVESTORS" />

    <PdAmenities />

    <VerificationBanner
      v-if="!isUSListing"
      :listing="listing" />

    <NuxtLazyHydrate when-visible>
      <PdRecommendationsV2
        :project-code="listing.code"
        :business-hub="listing.raw_listing?.location.business_hub"
        :data-attributes="{dataLh: { P: SCREEN.PDP_INVESTORS, Z: 'otros proyectos que te podrian interesar' }}"
        :refresh="optionalParams.refresh"
        class="page-pd-city-slug__section" />
    </NuxtLazyHydrate>

    <PdFixedCtasV2
      class="hidden-whatsapp z-30"
      :listing
      :observable-selectors="['.quotation-plan-typologies .typology-container', '.pd-sam-contact-form']"
      :data-attributes="{dataLh: { P: SCREEN.PDP_INVESTORS }}"
      @click-personalize-plan-cta="goToForm"
      @show-cta="updateBottomCtaVisibility" />

    <LazyAppModal
      v-if="showGallery"
      id="pd-gallery-modal"
      class="gallery-modal"
      :class="{'opacity-modal' : opacityValue}"
      :close-tracking-lh-id="$useLhId({ P: SCREEN.PDP_INVESTORS, Z: 'modal galeria', A: 'cerrar', C: 'cerrar_icon' })"
      @close="handleCloseGallery">
      <LazyAppGallery
        id="pdp-hero"
        :pictures="allImages"
        :videos="videos"
        :webinars="webinars"
        :tours="tours"
        :current-tab="currentTab"
        :is-mobile="!isDesktop"
        :location="locationParams"
        :data-attributes="{dataLh: { P: SCREEN.PDP_INVESTORS, Z: 'galeria principal'}}"
        :handle-close-gallery
        @opacity-event="toggleOpacity" />
    </LazyAppModal>

    <div
      v-if="!isContentLocked"
      class="transition-all duration-500 ease-in-out fixed right-[2rem] md:right-[6rem] bottom-[14rem] md:bottom-[10.4rem] z-60"
      :class="{ '!bottom-[2rem]': !isBottomCtaVisible}">
      <AppChatV2
        ref="appChat"
        bot-agent-name="Sam"
        :bot-agent-image="buildImageUrl('https://lahaus.imgix.net/static/lh-landing/img-sam-profile.png')"
        :listing
        :data-attributes="{dataLh: { P: SCREEN.PDP_INVESTORS }}"
        selector-to-trigger-button-chips=".investment-summary-new-version"
        :hiders-selectors="['.sam-contact-form__form-container .roomie-phone-input']"
        :face-to-face-visit-enabled="true" />
    </div>
  </main>
</template>

<style lang="scss">
.lh-roomie-v2 {
  .page-pd-v2-city-slug {
    margin-top: 0.4rem;

    @media (width >= theme('screens.lg')) {
      margin-top: 2.8rem;
    }

    &__section {
      @apply mx-auto;

      &--padded {
        @apply px-24;

        @media (width >= theme('screens.lg')) {
          padding-left: 13.1rem;
          padding-right: 13.1rem;
        }
      }
    }

    .gallery-modal {
      & .roomie-modal {
        @apply max-w-full rounded-none p-0 m-0 h-full max-h-full;

        &__body {
          @apply h-full;
        }

        &__content {
          @apply h-full relative;
        }

        &__button {
          @apply z-10;
        }
      }
    }

    #pd-map-section {
      @apply mx-24;

      @media (width >= theme('screens.lg')) {
        margin-right: 13.1rem;
        margin-left: 13.1rem;
      }
    }

    .feature-detail-section {
      @apply mx-24;

      @media (width >= theme('screens.lg')) {
        margin-right: 13.1rem;
        margin-left: 13.1rem;
      }
    }

    button#roomie-modal-navigation-button {
      @apply w-38;

      position: relative;
      top: 0;
      right: 0;

      .roomie-button-navigation__icon {
        @apply border-none #{!important};
        @apply outline-none #{!important};
        @apply bg-transparent #{!important};
        @apply p-0 #{!important};

        path {
          @apply fill-pine-600;

          transform: scale(1.58);
          transform-origin: center;
        }
      }

      @media (width >= theme('screens.lg')) {
        position: absolute;
        top: 3.2rem;
        right: 3.2rem;
      }
    }

    #pd-gallery-modal {
      .roomie-modal__header {
        @apply pt-16 pr-16;

        display: flex;
        justify-content: flex-end;

        .roomie-modal__title {
          @apply m-0;
        }

        @media (width >= theme('screens.lg')) {
          @apply p-0;
        }
      }

      .roomie-modal__body {
        @apply px-0 pb-0 #{!important};
      }
    }
  }

  footer .roomie-footer__bottom {
    padding-bottom: 14rem;
  }
}
</style>

<i18n src="./i18n.json"></i18n>
